import { Stack } from 'component-library';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { CustomBenchmarkType } from '@/api/rest/resources/types/benchmark';
import { BenchmarkType, PlotReportFactLabelNameEnum, R1FactType } from '@/api/rest/resources/types/fact';
import { GraphTileType } from '@/api/rest/resources/types/widget';
import { CarbonStorageBGHistoricalChart } from '@/components/Charts';
import { ChartHeading } from '@/components/Charts/components/ChartHeading';
import { useChartZoom } from '@/components/Charts/components/ChartZoom';
import { ItemizedGraphTypeRenderer } from '@/components/Charts/components/ItemizedGraphType';
import { NUM_ITEMS_TYPE } from '@/components/Charts/hooks/usePlotsForItemizedGraph';
import { GraphFact } from '@/components/Charts/types';
import { getChartDataWithBenchmarks, kgToTonneFormatter } from '@/components/Charts/utils';
import { GraphTileProps } from '@/components/IndicatorCard/GraphTile';
import { useSetIndicatorHasData } from '@/components/IndicatorCard/IndicatorContextProvider';
import { useNCCardContext } from '@/components/NCCard/NCCard';
import { useGetCustomBenchmarks } from '@/hooks/useCustomBenchmarks';
import { useScaledCustomBenchmark } from '@/hooks/useScaledCustomBenchmark';
import { useBenchmarksForOrganization } from '@/pages/shared/hooks/useBenchmarkForFact';
import {
  useOrganizationIndicatorGraphFact,
  useOrganizationIndicatorReportingFact,
} from '@/pages/shared/hooks/useOrganizationFact';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';

import { NoData } from '../../../../components/NoData';

export const CarbonCardGraphTile = (props?: (GraphTileProps & { selectedBenchmark?: string | null }) | null) => {
  const { t } = useTranslation();
  const { analysisType } = useNCCardContext();

  const { setIndicatorGraphData } = useSetIndicatorHasData();

  const { zoom, setZoom } = useChartZoom();
  const selectedZoom = props?.zoom ?? zoom; // props.zoom is used when this component is rendered as widget (e.g. measurement pages)
  const setZoomFunc = props?.setZoom ?? setZoom;
  const plotGroupId = props?.plotGroupId ?? null;

  const isTotal = analysisType === 'total';
  const carbonBgHistoricFact = isTotal
    ? R1FactType.r1_soil_carbon_historic_total_graph
    : R1FactType.r1_soil_carbon_historic_per_ha_graph;
  const benchmarkFactType = isTotal
    ? R1FactType.permanent_soil_carbon_total_benchmark
    : R1FactType.permanent_soil_carbon_per_ha_benchmark;

  const { [carbonBgHistoricFact]: carbonBgHistoric } = useOrganizationIndicatorGraphFact<GraphFact[]>(
    carbonBgHistoricFact,
    null,
    undefined,
    plotGroupId,
  );
  const carbonBgHistoricData = carbonBgHistoric?.value;

  const { [benchmarkFactType]: carbonBgLowBenchmark } = useOrganizationIndicatorReportingFact<number>(
    benchmarkFactType,
    null,
    { [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.low },
    plotGroupId,
  );

  const { [benchmarkFactType]: carbonBgModerateBenchmark } = useOrganizationIndicatorReportingFact<number>(
    benchmarkFactType,
    null,
    { [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.moderate },
    plotGroupId,
  );

  const { [benchmarkFactType]: carbonHighBenchmark } = useOrganizationIndicatorReportingFact<number>(
    benchmarkFactType,
    null,
    { [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.high },
    plotGroupId,
  );

  const cbgBenchmarks = useBenchmarksForOrganization(benchmarkFactType, null, kgToTonneFormatter, plotGroupId);

  const selectedBenchmark = props?.selectedBenchmark;
  const isScientific = !selectedBenchmark || selectedBenchmark === CustomBenchmarkType.SCIENTIFIC;

  const organizationId = useOrganizationId();
  const { data: customBenchmarksResp } = useGetCustomBenchmarks(
    organizationId,
    GraphTileType.historic_below_ground_carbon,
  );

  const activeCustomBenchmark = customBenchmarksResp?.results?.find((b) => b.id === selectedBenchmark);
  const displayCustomBenchmark = useScaledCustomBenchmark(activeCustomBenchmark, isTotal);

  /**
   * We only null check for cbgHistoricData since cbgHistoricData is the primary source data of the graph
   * reference lines are displayed only when cbgHistoricData is rendered
   */
  const noChartData = !carbonBgHistoricData?.length;

  useEffect(() => {
    setIndicatorGraphData({ graphData: !noChartData });
  }, [noChartData, setIndicatorGraphData]);

  if (noChartData) {
    return <NoData />;
  }

  const chartData = [
    ...carbonBgHistoricData,
    ...getChartDataWithBenchmarks({
      chartData: carbonBgHistoricData,
      benchmarks: {
        high: isScientific ? carbonHighBenchmark : undefined,
        moderate: isScientific ? carbonBgModerateBenchmark : undefined,
        low: isScientific ? carbonBgLowBenchmark : undefined,
      },
    }),
  ];

  return (
    <Stack center data-testid='carbon-graph-tile'>
      <Stack spacing={2.5} className='w-full items-center justify-center px-1 py-6 lg:items-start lg:p-6 lg:pl-1'>
        {!props?.headerProps?.hideChartHeader && (
          <ChartHeading
            heading={t('shared.ncaDetail.details.carbon.labels.historicalGraphHeadline')}
            popover={{
              title: t('shared.ncaDetail.details.carbon.explainers.belowTheGroundCarbonStorage.title'),
              body: t('shared.ncaDetail.details.carbon.explainers.belowTheGroundCarbonStorage.body'),
            }}
            className='w-full px-6 sm:px-0'
            zoom={selectedZoom}
            setZoom={setZoom}
          />
        )}
        <ItemizedGraphTypeRenderer
          graphType={props?.graphType}
          indicator={GraphTileType.historic_below_ground_carbon}
          plotRenderType={props?.plotRenderType ?? NUM_ITEMS_TYPE.TOP_PLOTS}
          zoom={selectedZoom}
          setZoom={setZoomFunc}
          customBenchmark={displayCustomBenchmark}
          defaultGraphType={
            <CarbonStorageBGHistoricalChart
              analysisType={analysisType}
              data={chartData}
              benchmarks={cbgBenchmarks}
              showTooltip={true}
              className='relative'
              data-testid='carbon-storage-bg-historic-chart'
              zoom={selectedZoom}
              setZoom={setZoomFunc}
              customBenchmark={displayCustomBenchmark}
            />
          }
        />
      </Stack>
    </Stack>
  );
};
